import React, { useContext, useRef, useState, useEffect } from "react";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import UserLanguageSelector from "../../components/UserLanguageSelector";
import AppTopbarSearch from "../../components/AppTopbarSearch";
import { IconAccount } from "../SidebarIcons";
import { getSystemVersionLabel } from "../../helpers/systemInfo";
import packageJson from "../../../package.json";

const { system } = packageJson;

function IconMenuToggle() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="3" y1="6" x2="21" y2="6" />
			<line x1="3" y1="12" x2="21" y2="12" />
			<line x1="3" y1="18" x2="21" y2="18" />
		</svg>
	);
}

function IconSun() {
	return (
		<svg
			className="absolute size-5 scale-100 rotate-0 text-default-700 transition-all duration-200 dark:scale-0 dark:-rotate-90"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
		>
			<circle cx="12" cy="12" r="5" />
			<line x1="12" y1="1" x2="12" y2="3" />
			<line x1="12" y1="21" x2="12" y2="23" />
			<line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
			<line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
			<line x1="1" y1="12" x2="3" y2="12" />
			<line x1="21" y1="12" x2="23" y2="12" />
			<line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
			<line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
		</svg>
	);
}

function IconMoon() {
	return (
		<svg
			className="absolute size-5 scale-0 -rotate-90 text-default-700 transition-all duration-200 dark:scale-100 dark:rotate-0"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
		>
			<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
		</svg>
	);
}

export default function MasterTopbar({
	onToggleSidenav,
	darkMode,
	onToggleDarkMode,
	onOpenAccount,
	onLogout,
}) {
	const { user } = useContext(AuthContext);
	const [menuOpen, setMenuOpen] = useState(false);
	const menuRef = useRef(null);

	useEffect(() => {
		const onDocClick = (e) => {
			if (menuRef.current && !menuRef.current.contains(e.target)) {
				setMenuOpen(false);
			}
		};
		document.addEventListener("mousedown", onDocClick);
		return () => document.removeEventListener("mousedown", onDocClick);
	}, []);

	return (
		<header className="app-header sticky top-0 z-30 box-border flex h-topbar-height min-h-topbar-height max-h-topbar-height shrink-0 items-center">
			<div className="flex w-full items-center justify-between gap-4 px-4 sm:px-6">
				<div className="flex min-w-0 items-center gap-4">
					<button
						type="button"
						id="button-toggle-menu"
						className="btn btn-icon size-8 shrink-0 rounded lg:hidden"
						onClick={onToggleSidenav}
						aria-label={i18n.t("master.layout.toggleMenu")}
					>
						<IconMenuToggle />
					</button>

					<AppTopbarSearch variant="master" />
				</div>

				<div className="flex items-center gap-2 sm:gap-3">
					<div className="topbar-item master-topbar-lang">
						<UserLanguageSelector variant="topbar" darkMode={darkMode} />
					</div>

					<div className="topbar-item">
						<button
							type="button"
							className="btn btn-icon relative size-8 rounded-full"
							onClick={onToggleDarkMode}
							aria-label={i18n.t("master.layout.toggleTheme")}
						>
							<IconSun />
							<IconMoon />
						</button>
					</div>

					<div className="topbar-item relative" ref={menuRef}>
						<button
							type="button"
							className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/15 text-primary ring-2 ring-primary/20 hover:bg-primary/25"
							onClick={() => setMenuOpen((v) => !v)}
							aria-expanded={menuOpen}
							aria-haspopup="true"
						>
							<IconAccount />
						</button>
						{menuOpen && (
							<div
								className="master-user-menu absolute end-0 top-[calc(100%+8px)] z-50 min-w-52 overflow-hidden rounded-md border py-1 shadow-lg"
								role="menu"
							>
								<div className="master-user-menu__head border-b px-4 py-3">
									<p className="m-0 text-sm font-semibold">
										{user?.name}
									</p>
									<p className="m-0 mt-0.5 truncate text-xs opacity-70">
										{user?.email}
									</p>
								</div>
								<div className="px-4 py-2.5 text-xs text-default-500">
									<span className="block font-medium text-default-600 dark:text-default-400">
										{i18n.t("layout.topbarSearch.systemVersion")}
									</span>
									<span className="mt-0.5 block">
										{system.name} ({getSystemVersionLabel()})
									</span>
								</div>
								<div className="my-1 border-t border-default-200 dark:border-default-700" />
								<button
									type="button"
									className="master-user-menu__item flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium transition-colors"
									onClick={() => {
										setMenuOpen(false);
										onOpenAccount();
									}}
								>
									<IconAccount />
									{i18n.t("mainDrawer.appBar.user.profile")}
								</button>
								<button
									type="button"
									className="master-user-menu__item master-user-menu__item--danger flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium transition-colors"
									onClick={() => {
										setMenuOpen(false);
										onLogout();
									}}
								>
									{i18n.t("mainDrawer.appBar.user.logout")}
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</header>
	);
}
